import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Tooltip, CircularProgress, Alert, Snackbar,
  Table, TableHead, TableBody, TableRow, TableCell,
  Switch, FormControlLabel, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import api from '../../services/api.js';
import { ckConfirm, useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import { ExamExtraPagesNext } from './ExamAssetsNext.jsx';

const POSITION_LABELS = { cover: 'דף שער', end: 'דף סיום' };

/** In the new design this renders ExamExtraPagesNext. */
const ExamExtraPages = () => (useCkInside() ? <ExamExtraPagesNext /> : <ExamExtraPagesLegacy />);

const ExamExtraPagesLegacy = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formFile, setFormFile] = useState(null);
  const [formDefault, setFormDefault] = useState(false);
  const [formPosition, setFormPosition] = useState('cover');
  const [saving, setSaving] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/exam-extra-pages');
      setTemplates(data.data || []);
    } catch {
      setError('שגיאה בטעינת דפי שער/סיום');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const openCreate = () => {
    setEditing(null);
    setFormName('');
    setFormDesc('');
    setFormFile(null);
    setFormDefault(false);
    setFormPosition('cover');
    setDialog(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setFormName(t.name);
    setFormDesc(t.description || '');
    setFormFile(null);
    setFormDefault(t.isDefault || false);
    setFormPosition(t.position || 'cover');
    setDialog(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { setError('שם חובה'); return; }
    if (!editing && !formFile) { setError('יש להעלות קובץ PDF'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', formName.trim());
      fd.append('description', formDesc.trim());
      fd.append('isDefault', String(formDefault));
      fd.append('position', formPosition);
      if (formFile) fd.append('file', formFile);

      if (editing) {
        await api.put(`/exam-extra-pages/${editing._id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setSuccess('דף עודכן בהצלחה');
      } else {
        await api.post('/exam-extra-pages', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setSuccess('דף נוסף בהצלחה');
      }
      setDialog(false);
      loadTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!(await ckConfirm({ title: 'מחיקת דף', message: `למחוק את הדף "${t.name}"?`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/exam-extra-pages/${t._id}`);
      setSuccess('דף נמחק');
      loadTemplates();
    } catch {
      setError('שגיאה במחיקה');
    }
  };

  const handleDownload = async (t) => {
    try {
      const response = await api.get(`/exam-extra-pages/${t._id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = t.fileOriginalName || `${t.name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('שגיאה בהורדה');
    }
  };

  const handleToggleDefault = async (t) => {
    try {
      await api.put(`/exam-extra-pages/${t._id}`, { isDefault: !t.isDefault });
      setSuccess(t.isDefault ? 'הוסר כברירת מחדל' : 'הוגדר כברירת מחדל');
      loadTemplates();
    } catch {
      setError('שגיאה בעדכון');
    }
  };

  const handleToggleStatus = async (t) => {
    try {
      const newStatus = t.status === 'active' ? 'inactive' : 'active';
      await api.put(`/exam-extra-pages/${t._id}`, { status: newStatus });
      setSuccess(newStatus === 'active' ? 'דף הופעל' : 'דף הושבת');
      loadTemplates();
    } catch {
      setError('שגיאה בעדכון');
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="var(--ck-primary, #1a237e)">
            דפי שער וסיום (PDF)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            קבצי PDF שיתווספו לפני או אחרי הבחינה בהנפקה
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ bgcolor: 'var(--ck-primary, #1565c0)', '&:hover': { bgcolor: 'var(--ck-primary-hover, #0d47a1)' }, borderRadius: 2 }}
        >
          העלאת דף PDF
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <PictureAsPdfIcon sx={{ fontSize: 48, color: 'var(--ck-text-3, #bdbdbd)', mb: 1 }} />
          <Typography variant="subtitle1" color="text.secondary">אין דפי שער/סיום עדיין</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            העלה קובץ PDF לשימוש חוזר כדף שער או דף סיום
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
            העלאת דף ראשון
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 'var(--ck-shadow-1, 0 2px 12px rgba(0,0,0,0.08))' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'var(--ck-surface-2, #f5f5f5)' }}>
                <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>שם</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>תיאור</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 100 }}>מיקום</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 80 }}>סטטוס</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 80 }}>ברירת מחדל</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 140 }}>פעולות</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((t, idx) => (
                <TableRow
                  key={t._id}
                  hover
                  sx={{
                    '&:hover': { bgcolor: 'var(--ck-surface-2, #fafafa)' },
                    opacity: t.status === 'inactive' ? 0.55 : 1,
                  }}
                >
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PictureAsPdfIcon sx={{ color: 'var(--ck-danger, #c62828)' }} />
                      <Typography fontWeight={600}>{t.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                      {t.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={POSITION_LABELS[t.position] || t.position}
                      size="small"
                      sx={{
                        bgcolor: t.position === 'cover' ? 'var(--ck-primary-soft, #e8eaf6)' : 'var(--ck-danger-soft, #fce4ec)',
                        color: t.position === 'cover' ? 'var(--ck-primary, #283593)' : '#ad1457',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t.status === 'active' ? 'פעיל' : 'מושבת'}
                      size="small"
                      color={t.status === 'active' ? 'success' : 'default'}
                      onClick={() => handleToggleStatus(t)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t.isDefault ? 'הסר ברירת מחדל' : 'הגדר כברירת מחדל'}>
                      <IconButton size="small" onClick={() => handleToggleDefault(t)}>
                        {t.isDefault
                          ? <StarIcon sx={{ color: 'var(--ck-warning, #ffa000)' }} />
                          : <StarBorderIcon sx={{ color: 'var(--ck-text-3, #bdbdbd)' }} />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="הורד">
                        <IconButton size="small" onClick={() => handleDownload(t)} sx={{ color: 'var(--ck-primary, #1565c0)' }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="ערוך">
                        <IconButton size="small" onClick={() => openEdit(t)} sx={{ color: 'var(--ck-primary, #1976d2)' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="מחק">
                        <IconButton size="small" onClick={() => handleDelete(t)} sx={{ color: 'var(--ck-danger, #ef5350)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog
        open={dialog}
        onClose={() => !saving && setDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'עריכת דף PDF' : 'העלאת דף PDF חדש'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            autoFocus
            fullWidth
            label="שם הדף"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder='לדוג׳: שער מוסד / דף הוראות'
          />
          <TextField
            fullWidth
            label="תיאור (אופציונלי)"
            value={formDesc}
            onChange={(e) => setFormDesc(e.target.value)}
            multiline
            minRows={2}
          />
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            sx={{
              borderColor: formFile ? 'var(--ck-danger, #c62828)' : 'var(--ck-border-strong, #bdbdbd)',
              color: formFile ? 'var(--ck-danger, #c62828)' : 'text.secondary',
              bgcolor: formFile ? 'var(--ck-danger-soft, #ffebee)' : 'transparent',
            }}
          >
            {formFile ? formFile.name : (editing ? 'החלף PDF (אופציונלי)' : 'בחר קובץ PDF...')}
            <input
              type="file"
              hidden
              accept=".pdf,application/pdf"
              onChange={(e) => setFormFile(e.target.files[0] || null)}
            />
          </Button>

          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>מיקום הדף</Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {[{ val: 'cover', label: 'דף שער (לפני)' }, { val: 'end', label: 'דף סיום (אחרי)' }].map((opt) => (
              <Paper
                key={opt.val}
                variant="outlined"
                onClick={() => setFormPosition(opt.val)}
                sx={{
                  flex: 1, p: 1.5, textAlign: 'center', cursor: 'pointer',
                  borderRadius: 2, transition: 'all 0.15s',
                  borderColor: formPosition === opt.val ? 'var(--ck-primary, #1565c0)' : 'var(--ck-border, #e0e0e0)',
                  bgcolor: formPosition === opt.val ? 'var(--ck-primary-soft, #e3f2fd)' : 'transparent',
                  fontWeight: formPosition === opt.val ? 700 : 400,
                }}
              >
                {opt.label}
              </Paper>
            ))}
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formDefault}
                onChange={(e) => setFormDefault(e.target.checked)}
                color="warning"
              />
            }
            label="הגדר כברירת מחדל למיקום זה"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog(false)} disabled={saving}>ביטול</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          >
            {saving ? 'שומר...' : (editing ? 'עדכן' : 'העלה')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ExamExtraPages;
